export type SupportArticleSection = {
  heading: string
  paragraphs: string[]
  listItems: string[]
  listType?: 'ordered' | 'unordered'
}

export type ParsedSupportArticle = {
  slug: string
  title: string
  summary: string
  sections: SupportArticleSection[]
  searchText: string
}

const normalizeTitle = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

export const parseSupportArticle = (markdown: string, slug: string): ParsedSupportArticle => {
  const lines = markdown.split(/\r?\n/)
  let title = slug
  let current: SupportArticleSection | null = null
  const sections: SupportArticleSection[] = []

  const flush = () => {
    if (!current) return
    sections.push(current)
    current = null
  }

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    if (trimmed.startsWith('# ')) {
      title = trimmed.slice(2).trim()
      continue
    }
    if (trimmed.startsWith('## ')) {
      flush()
      current = {
        heading: trimmed.slice(3).trim(),
        paragraphs: [],
        listItems: [],
      }
      continue
    }
    if (!current) continue
    if (/^\d+\.\s+/.test(trimmed)) {
      if (!current.listType) current.listType = 'ordered'
      current.listItems.push(trimmed.replace(/^\d+\.\s+/, '').trim())
      continue
    }
    if (trimmed.startsWith('- ')) {
      if (!current.listType) current.listType = 'unordered'
      current.listItems.push(trimmed.slice(2).trim())
      continue
    }
    current.paragraphs.push(trimmed)
  }
  flush()

  const summarySection = sections.find(
    (section) => normalizeTitle(section.heading) === 'quick answer'
  ) || sections.find(
    (section) => normalizeTitle(section.heading) === 'what this helps you do'
  )
  const summary = summarySection?.paragraphs[0] ?? ''

  const searchTextParts: string[] = [title]
  sections.forEach((section) => {
    searchTextParts.push(section.heading, ...section.paragraphs, ...section.listItems)
  })

  return {
    slug,
    title,
    summary,
    sections,
    searchText: searchTextParts.join(' ').toLowerCase(),
  }
}
